import { Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Tiles } from "@/components/motion/Tiles";
import { Seo } from "@/components/Seo";
import { QuoteForm } from "@/components/forms/QuoteForm";
import { ContactForm } from "@/components/forms/ContactForm";
import { Lines, Reveal } from "@/components/motion/Reveal";
import { COMPANY, whatsappUrl } from "@/data/company";

const CHANNELS = [
  { icon: Phone, label: "Telefono", value: COMPANY.phoneDisplay, href: `tel:${COMPANY.phoneIntl}`, testId: "contact-phone-link" },
  { icon: MessageCircle, label: "WhatsApp", value: "Scrivici ora", href: whatsappUrl(), testId: "contact-whatsapp-link", external: true },
  { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}`, testId: "contact-email-link" },
];

export default function Contatti() {
  return (
    <>
      <Seo title="Contatti e preventivo" description="Contatta DESP di Sandro Pace a Catania per noleggio piattaforme aeree ed escavatori: telefono 351 583 7771, WhatsApp, email. Preventivo gratuito in giornata." path="/contatti" />

      <section className="relative overflow-hidden bg-ink pt-32 pb-20 text-white sm:pt-40 sm:pb-28 grain" data-testid="contatti-hero">
        <div className="absolute inset-0 blueprint opacity-50" />
        <div className="container-x relative grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal y={10}><p className="eyebrow text-blue-500 flex items-center gap-3"><Tiles />Contatti</p></Reveal>
            <Lines inView={false} delay={0.15} lines={["Parliamo del", "tuo cantiere."]} className="display mt-4 text-4xl sm:text-5xl lg:text-6xl" />
            <Reveal delay={0.4} className="mt-6 text-white/65 sm:text-lg"><p>Compila la richiesta di preventivo oppure chiama direttamente: risponde Sandro, non un centralino.</p></Reveal>
            <div className="mt-10 space-y-px bg-white/10" data-testid="contact-channels">
              {CHANNELS.map((c) => (
                <Reveal key={c.label} delay={0.5}>
                  <a href={c.href} target={c.external ? "_blank" : undefined} rel={c.external ? "noopener noreferrer" : undefined} className="group flex items-center gap-5 bg-ink px-5 py-5 transition-colors hover:bg-ink-2" data-testid={c.testId}>
                    <span className="flex h-11 w-11 items-center justify-center border border-white/15 text-blue-500 transition-colors group-hover:border-blue-500 group-hover:bg-blue-500 group-hover:text-white"><c.icon className="h-5 w-5" /></span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-widest text-white/45">{c.label}</span>
                      <span className="block font-display text-xl font-bold">{c.value}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.6} className="mt-10 grid gap-6 sm:grid-cols-2">
              <div className="flex gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                <div data-testid="contact-hours">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">Orari</p>
                  {COMPANY.hours.map((h) => (
                    <p key={h.days} className="mt-1 text-sm text-white/80"><span className="text-white/50">{h.days}:</span> {h.time}</p>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">Zona</p>
                  <p className="mt-1 text-sm text-white/80">{COMPANY.area}</p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.3} className="lg:col-span-7">
            <div className="border border-white/10 bg-ink-2/60 p-6 backdrop-blur sm:p-10" id="preventivo">
              <p className="eyebrow text-blue-500 flex items-center gap-3"><Tiles />Richiesta preventivo</p>
              <h2 className="display mt-2 text-3xl sm:text-4xl">Preventivo gratuito in giornata</h2>
              <div className="mt-8"><QuoteForm /></div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="contatti-form-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal y={10}><p className="eyebrow flex items-center gap-3"><Tiles />Informazioni</p></Reveal>
            <Lines as="h2" lines={["Hai una domanda?", "Scrivici."]} className="display mt-3 text-4xl text-ink sm:text-5xl" />
            <Reveal delay={0.2} className="mt-5 text-ink/60"><p>Per informazioni generali, collaborazioni o qualsiasi altra richiesta che non sia un preventivo.</p></Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-8"><ContactForm /></Reveal>
        </div>
      </section>
    </>
  );
}
